import { useState, useCallback } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  ManageOnMobileModalState,
  StripePaystationState,
  xsollaModalOpen,
} from "../../State";
import { useUser } from "../useUser";
import { analytics } from "../../Analytics";
import { useResolvedPaymentProvider } from "../useResolvedPaymentProvider";

const ENDPOINT = process.env.REACT_APP_STRIPE_ENDPOINT;

export const useManageSubscription = () => {
  const { allstarUser } = useUser();
  const [paystation, setPaystationOpen] = useRecoilState(StripePaystationState);
  const [xsollaPaystation, setXsollaPaystationOpen] =
    useRecoilState(xsollaModalOpen);
  const [retentionOpen, setRetentionOpen] = useState(false);
  const setManageOnMobileOpen = useSetRecoilState(ManageOnMobileModalState);
  const { resolve: resolveProvider } = useResolvedPaymentProvider();

  const userGroupType = allstarUser?.user?.userGroup?.type;
  const isTrialPeriod = allstarUser?.user?.userGroup?.isTrialPeriod;
  const existingProvider = allstarUser?.user?.credits?.providerName;
  // Kept for callers that still want to know the *current* sub's provider
  // (e.g. analytics or copy). Routing should use `resolveProvider()` below.
  const isStripeUser = existingProvider === "STRIPE";
  // Mobile in-app purchases can only be managed through the originating store,
  // so we route these users to an informational modal instead of the web flow.
  // The kanye gateway owns the provider→store mapping via these booleans.
  const isAppleIapUser =
    allstarUser?.user?.usingAppStoreInAppPurchases === true;
  const isPlayStoreUser =
    allstarUser?.user?.usingPlayStoreInAppPurchases === true;
  const isMobileIapUser = isAppleIapUser || isPlayStoreUser;

  const canManageBilling = allstarUser?.user?.canManageBilling === true;
  const isActiveSubscriber = userGroupType !== "FREE" || canManageBilling;

  const fetchStripePortalUrl = useCallback(async () => {
    const res = await fetch(`${ENDPOINT}/stripe/v1/request`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${allstarUser.token}`,
      },
    });
    const data = await res.json();
    return data.portal as string;
  }, [allstarUser.token]);

  const proceedToManage = useCallback(async () => {
    // Source of truth for provider:
    //   1. If the user already has a subscription, manage it where it lives
    //      (a Stripe sub can't be managed via Xsolla and vice versa).
    //   2. Otherwise honor the geo-routing lambda's choice.
    let provider: "stripe" | "xsolla";
    if (existingProvider === "STRIPE") provider = "stripe";
    else if (existingProvider === "XSOLLA") provider = "xsolla";
    else provider = await resolveProvider();

    if (provider === "stripe") {
      setPaystationOpen({
        ...paystation,
        type: "session",
        isModalOpen: true,
        portalUrl: undefined,
      });
      const url = await fetchStripePortalUrl();
      setPaystationOpen({
        ...paystation,
        type: "session",
        isModalOpen: true,
        portalUrl: url,
      });
      window?.open(url, "_blank")?.focus();
    } else {
      setXsollaPaystationOpen({
        ...xsollaPaystation,
        type: "manage",
        isModalOpen: true,
      });
    }
  }, [
    existingProvider,
    resolveProvider,
    paystation,
    setPaystationOpen,
    fetchStripePortalUrl,
    xsollaPaystation,
    setXsollaPaystationOpen,
  ]);

  const handleManageSubscription = useCallback(
    async (trackingLocation?: string) => {
      analytics.track("Manage Subscription Click", {
        location: trackingLocation || "Unknown",
      });

      // Subscriptions purchased through a mobile store can only be managed in
      // that store — send the user there instead of the web manage flow.
      if (isMobileIapUser) {
        const store = isAppleIapUser ? "apple" : "google";
        analytics.track("Manage Subscription Mobile Redirect", {
          location: trackingLocation || "Unknown",
          store,
        });
        setManageOnMobileOpen({ isOpen: true, store });
        return;
      }

      setRetentionOpen(true);
    },
    [isMobileIapUser, isAppleIapUser, setManageOnMobileOpen],
  );

  const handleRetentionClose = useCallback(() => {
    setRetentionOpen(false);
  }, []);

  const handleRetentionSkip = useCallback(() => {
    setRetentionOpen(false);
    proceedToManage();
  }, [proceedToManage]);

  return {
    isActiveSubscriber,
    isStripeUser,
    handleManageSubscription,
    fetchStripePortalUrl,
    retentionOpen,
    handleRetentionClose,
    handleRetentionSkip,
  };
};
